import moment from 'moment';
import { DEFAULT_PAGE_SIZE } from 'constants/tables';
import {
  WALLET_CREDIT_TRANSACTION_TYPES,
  WALLET_DEBIT_TRANSACTION_TYPES,
  WALLET_STATUS,
  WALLET_TOP_UP_PAYMENT_METHODS,
  WALLET_TRANSACTION_STATUSES,
  WALLET_TRANSACTION_TYPES,
} from 'constants/wallet';
import { formatCurrencyAmount } from 'utils/currencyUtil';
import { findByLowerCase } from 'utils/walletUtil';

const HISTORY_KEY_PREFIX = 'corporateAccounts.wallet.dashboard.history';

// Wallet Service amounts are integer minor units (pence/cents) in the wallet's own currency.
export const formatMinorUnits = (amountMinor, currency) => {
  if (amountMinor === null || amountMinor === undefined || Number.isNaN(Number(amountMinor))) {
    return '';
  }

  return formatCurrencyAmount(Number(amountMinor) / 100, currency);
};

const isValidMinorUnits = amountMinor =>
  amountMinor !== null && amountMinor !== undefined && !Number.isNaN(Number(amountMinor));

// History columns carry the currency in their header ("Amount (£)"), so cells are bare numbers in
// the currency's own format: "332.50" for GBP, "332,50" for EUR.
export const formatHistoryBalance = (amountMinor, currency) =>
  isValidMinorUnits(amountMinor)
    ? formatCurrencyAmount(Number(amountMinor) / 100, currency, { showSymbol: false })
    : '';

// A history amount carries its direction whatever sign the service sent: credits (top-ups,
// refunds) read "+250.00", debits (parking sessions) "-4.50"; unknown types keep the service's sign.
export const formatTransactionAmount = (amountMinor, currency, transactionType) => {
  if (!isValidMinorUnits(amountMinor)) {
    return '';
  }
  const type = normalizeTransactionType(transactionType);
  const magnitude = Math.abs(Number(amountMinor)) / 100;
  const options = { showSymbol: false, signDisplay: 'exceptZero' };

  if (WALLET_CREDIT_TRANSACTION_TYPES.includes(type)) {
    return formatCurrencyAmount(magnitude, currency, options);
  }
  if (WALLET_DEBIT_TRANSACTION_TYPES.includes(type)) {
    return formatCurrencyAmount(-magnitude, currency, options);
  }
  return formatCurrencyAmount(Number(amountMinor) / 100, currency, { showSymbol: false });
};

const LAST_TOP_UP_KEY_PREFIX = 'corporateAccounts.wallet.dashboard.lastTopUp';

// 'Card' / 'Bank Transfer' -> 'card' / 'banktransfer'; null for anything unknown.
export const normalizeTopUpPaymentMethod = paymentMethod =>
  findByLowerCase(WALLET_TOP_UP_PAYMENT_METHODS, String(paymentMethod ?? '').replace(/\s/g, ''));

// Known payment methods are translated; anything else is shown as the service sent it.
export const formatTopUpDetail = (lastTopUp, translateText) => {
  if (!lastTopUp) {
    return null;
  }
  const normalized = normalizeTopUpPaymentMethod(lastTopUp.paymentMethod);
  const paymentMethod = normalized
    ? translateText(`${LAST_TOP_UP_KEY_PREFIX}.paymentMethod.${normalized}`)
    : lastTopUp.paymentMethod;
  return `${lastTopUp.date} · ${paymentMethod}`;
};

export const normalizeWalletStatus = status => findByLowerCase(WALLET_STATUS, status);

// Wallet Service timestamps are ISO 8601; the dashboard shows dates as dd/mm/yyyy like the history.
export const formatStatusDate = isoDate => {
  if (!isoDate) {
    return null;
  }
  const parsed = moment(isoDate);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : null;
};

export const normalizeTransactionType = transactionType =>
  findByLowerCase(WALLET_TRANSACTION_TYPES, transactionType);

export const normalizeTransactionStatus = status =>
  findByLowerCase(WALLET_TRANSACTION_STATUSES, status);

export const getTransactionTypeKey = transactionType => {
  const normalized = normalizeTransactionType(transactionType);
  return normalized ? `${HISTORY_KEY_PREFIX}.transactionType.${normalized}` : null;
};

export const getTransactionStatusKey = status => {
  const normalized = normalizeTransactionStatus(status);
  return normalized ? `${HISTORY_KEY_PREFIX}.status.${normalized}` : null;
};

export const mapWalletToDashboard = wallet => {
  if (!wallet) {
    return null;
  }

  const { currency } = wallet;
  const history = wallet.balanceHistory ?? {};
  const lastTopUp = history.lastTopUp ?? null;

  return {
    walletId: wallet.walletId,
    walletName: wallet.walletName ?? '',
    status: normalizeWalletStatus(wallet.status) ?? wallet.status,
    // Not in the wallet-service response yet — requested in FS-170; null until it arrives.
    statusChangedAt: wallet.statusChangedAt ?? null,
    currency,
    balance: formatMinorUnits(wallet.availableBalance ?? 0, currency),
    balanceMajor: (wallet.availableBalance ?? 0) / 100,
    alertThreshold: formatMinorUnits(wallet.lowBalanceAlertThresholdMinor ?? 0, currency),
    alertThresholdMajor: (wallet.lowBalanceAlertThresholdMinor ?? 0) / 100,
    spentThisMonth: formatMinorUnits(history.spentThisMonth ?? 0, currency),
    lastTopUp: lastTopUp
      ? {
          amount: formatMinorUnits(lastTopUp.amount, currency),
          date: lastTopUp.date,
          paymentMethod: lastTopUp.paymentMethod,
        }
      : null,
    history: {
      items: (history.items ?? []).map(item => ({
        id: item.transactionId,
        date: item.date,
        transactionType: normalizeTransactionType(item.transactionType) ?? item.transactionType,
        amount: formatTransactionAmount(item.amount, currency, item.transactionType),
        balance: formatHistoryBalance(item.balance, currency),
        status: normalizeTransactionStatus(item.status) ?? item.status,
        topUpRejectReason: item.topUpRejectReason ?? null,
      })),
      pageNumber: history.pageNumber ?? 1,
      pageSize: history.pageSize ?? DEFAULT_PAGE_SIZE,
      totalCount: history.totalCount ?? 0,
      totalPages: history.totalPages ?? 0,
    },
  };
};
